import { useState, useEffect, ReactNode, SyntheticEvent } from 'react'
import { Box, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import InputLabel from '@mui/material/InputLabel'
import classNames from 'classnames'
import { useField, useFormikContext } from 'formik'
import { t } from 'i18next'
import { AngleDownIcon2, LfCloseIcon } from 'src/components/shared/ui/Icons'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { fieldsStyles as styles } from 'src/components/shared/ui/form/fields/fieldsStyles'

type Props = {
  id: string
  label: string
  items: any[] | undefined
  onChange?: (field: string, value: any) => void
  itemName?: string
  itemKey?: string
  multiple?: boolean
  disabled?: boolean
  required?: boolean
  loading?: boolean
  groupBy?: (option: any) => string
  groupName?: string
  getOptionLabelCallback?: (option: any) => string
  renderOptionCallback?: (props, option) => ReactNode
  placeholder?: string
  sortBy?: boolean | string | ((itemsOption) => any[])
}
function AutoCompleteField({
  id,
  label,
  items,
  onChange,
  itemName = 'name',
  itemKey = '',
  multiple = false,
  disabled = false,
  required = false,
  loading = false,
  groupBy = () => '',
  groupName = 'name',
  sortBy = undefined,
  getOptionLabelCallback = undefined,
  renderOptionCallback = undefined,
  placeholder = t('form.placeholders.elige_opcion')
}: Props): JSX.Element {
  const [sort, setSort] = useState(false)
  const [autoload, setAutoload] = useState(false)
  const [fieldInfo, meta] = useField(id)
  const { setFieldValue, setFieldTouched } = useFormikContext()
  const autocompleteClassNames = classNames({
    disabled,
    required,
    error: meta.touched && meta.error
  })
  const parseUpperCase = item => {
    item.forEach(i => {
      i[itemName] = i[itemName].toUpperCase()
    })
    return item
  }
  let itemsOption: any[] = items === undefined ? [] : parseUpperCase(items)
  const handleOnChange = (_event: SyntheticEvent<Element, Event>, newValue: any) => {
    setFieldValue(id, newValue, true)
    const isEmpty = multiple ? !newValue || newValue.length === 0 : !newValue
    if (isEmpty) {
      setFieldTouched(id, true, false)
    }
    if (onChange) {
      onChange(id, newValue)
    }
    setSort(true)
  }
  const renderOption = (props, option) => {
    if (renderOptionCallback) {
      return renderOptionCallback(props, option)
    }

    let value = option[itemName] || option
    let key = option[itemName] || option

    if (itemKey.length) {
      key = option[itemKey]
    }

    if (groupBy(option)) {
      value = option[groupName]
      key = `${option[itemName]}-${value}`
    }
    return (
      <li {...props} key={key}>
        {value}
      </li>
    )
  }

  const getOptionLabel = option => {
    if (!getOptionLabelCallback) {
      if (groupBy(option)) {
        return option[groupName]
      }
      return option && option[itemName] ? option[itemName] : option
    }
    return getOptionLabelCallback(option)
  }

  useEffect(() => {
    if (sort && itemsOption && sortBy) {
      if (typeof sortBy === 'function') {
        // eslint-disable-next-line
        itemsOption = sortBy(itemsOption)
      }

      let field = ''

      if (typeof sortBy === 'boolean' && itemName) {
        field = itemName
      } else if (typeof sortBy === 'string') {
        field = sortBy
      }

      if (field) {
        itemsOption = itemsOption.sort((a, b) => {
          if (a[field] > b[field]) {
            return 1
          }
          if (a[field] < b[field]) {
            return -1
          }
          return 0
        })
      }
      setSort(false)
    }
  }, [sort])

  useEffect(
    () => {
      if (items?.length === 1 && !autoload) {
        setAutoload(true)
        setFieldValue(id, items[0])
        setTimeout(() => {
          setAutoload(false)
        })
      }
    },
    // eslint-disable-next-line
    [items]
  )

  return (
    <>
      <InputLabel id={`${id}-label`} className={autocompleteClassNames} sx={styles.inputLabel}>
        {label}
      </InputLabel>
      <Autocomplete
        autoSelect
        disabled={disabled || loading}
        id={id}
        value={fieldInfo.value}
        options={itemsOption}
        popupIcon={
          loading ? (
            <Box
              sx={styles.wrapperLoader}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Loader size="small" thin />
            </Box>
          ) : (
            <AngleDownIcon2 id={`${id}-icon`} sx={styles.popupIcon} />
          )
        }
        clearIcon={<LfCloseIcon sx={styles.closeIcon} />}
        isOptionEqualToValue={(option, value) =>
          typeof option === 'string' ? option === value : option[itemName] === value[itemName]
        }
        renderOption={renderOption}
        renderInput={params => (
          <TextField
            {...params}
            value={fieldInfo.value}
            onBlur={fieldInfo.onBlur}
            label=""
            placeholder={placeholder}
          />
        )}
        onChange={handleOnChange}
        onOpen={fieldInfo.onBlur}
        multiple={multiple}
        groupBy={groupBy}
        getOptionLabel={getOptionLabel}
        className={autocompleteClassNames}
        clearText={t('actions.clean')}
        closeText={t('actions.close')}
        loadingText={t('actions.loading')}
        noOptionsText={t('actions.no_options')}
        openText={t('actions.open')}
        sx={styles.input}
      />
      <Box component="span" sx={styles.helpText}>
        {meta.touched ? meta.error : null}
      </Box>
    </>
  )
}
export default AutoCompleteField
